<script setup lang="ts">
  import { nextTick, ref, watch } from 'vue';
  import { useOnline } from '@vueuse/core';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  /**
   * The chromeless frame of the store-setup flow (plan §4.0): top bar ·
   * scrolling body · bottom bar, viewport-exact inside the full-page POS
   * shell, so the document never scrolls. When `screenKey` changes it scrolls
   * the body to the top and focuses the new screen's `<h1 tabindex="-1">`.
   */
  const props = defineProps<{
    /** The wide tier (stable width ≥ 1024): roomier body padding. */
    wide: boolean;
    /** Changes on every screen switch. */
    screenKey: string;
  }>();
  const online = useOnline();
  const body = ref<HTMLElement | null>(null);

  function scrollToTop() {
    if (body.value) body.value.scrollTop = 0;
  }

  function focusHeading() {
    const heading = body.value?.querySelector('h1');
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
  }

  watch(
    () => props.screenKey,
    async () => {
      await nextTick();
      // Focus first: browsers without `preventScroll` scroll the heading into view.
      focusHeading();
      scrollToTop();
    }
  );

  defineExpose({ scrollToTop });
</script>

<template>
  <div class="v2-setup-shell" :class="{ wide }">
    <slot name="top" />
    <div ref="body" class="shell-body">
      <p v-if="!online" class="offline-banner" role="status">
        <PosUiIcon name="wifi-off" class="offline-icon" />
        <span>{{ $t('storeSetup.offline') }}</span>
      </p>
      <slot />
    </div>
    <slot name="bottom" />
  </div>
</template>

<style scoped lang="scss">
  // The `.reservations-guide` archetype: bars outside the scroll, one scrolling body.
  .v2-setup-shell {
    @apply tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col tw-bg-canvas;
    .shell-body {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-px-4 tw-py-4 md:tw-px-6;
      overscroll-behavior: contain;
    }
    &.wide .shell-body {
      @apply tw-py-6;
    }
    .offline-banner {
      @apply tw-m-0 tw-mb-4 tw-flex tw-shrink-0 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-700;
      .offline-icon {
        @apply tw-shrink-0 tw-text-lg tw-text-neutral-500;
      }
    }
    // Screen headings take focus on a screen switch; the move itself is the cue.
    :deep(h1[tabindex='-1']:focus) {
      @apply tw-outline-none;
    }
  }
</style>
